import type { AdminTransaction } from '../services/api';

export const formatAmount = (amount: number | string | undefined | null, currency: string = 'NGN'): string => {
  if (amount === undefined || amount === null || amount === '') return '₦0.00';
  const num = typeof amount === 'number' ? amount : parseFloat(String(amount));
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency === 'NGN' ? 'NGN' : currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const getRunningBalance = (tx: AdminTransaction): string => {
  const val = tx.runningBalance ?? tx.metadata?.runningBalance ?? tx.metadata?.onafriqData?.runningBalance;
  if (val !== undefined && val !== null && val !== '') {
    const num = typeof val === 'number' ? val : parseFloat(String(val));
    if (!isNaN(num)) {
      return formatAmount(num, tx.currency || 'NGN');
    }
  }
  return '—';
};

export const getTransactionCid = (tx: AdminTransaction): string => {
  return (
    tx.onafriqAccountId ||
    tx.cid ||
    tx.metadata?.onafriqData?.accountNumber ||
    tx.metadata?.accountNumber ||
    (tx.userId ? tx.userId.substring(0, 8) : '—')
  );
};

export const generateReceiptHtml = (tx: AdminTransaction): string => {
  const formattedDate = new Date(tx.createdAt).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const formattedTime = tx.transactionTime || new Date(tx.createdAt).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const amountStr = formatAmount(tx.amount, tx.currency || 'NGN');
  const runningBalStr = getRunningBalance(tx);
  const companyName = tx.user?.company?.legalName || (tx.user ? `${tx.user.firstName} ${tx.user.lastName}` : 'Individual / ESN Fleet');
  const userEmail = tx.user?.email || 'N/A';
  const cid = getTransactionCid(tx);
  const station = tx.merchantName || tx.metadata?.station || tx.description || 'N/A';
  const vehicle = tx.vehicle?.plateNumber || tx.metadata?.vehiclePlate || 'N/A';
  const litres = tx.metadata?.litres ? `${tx.metadata.litres} L` : null;
  const price = tx.metadata?.price ? formatAmount(tx.metadata.price, tx.currency || 'NGN') : null;
  const odometer = tx.metadata?.importedOdometer || tx.metadata?.odometer ? `${tx.metadata.importedOdometer || tx.metadata.odometer} km` : null;
  const statusStr = (tx.status || 'completed').toUpperCase();
  const statusColor = statusStr === 'COMPLETED' ? '#047857' : statusStr === 'FAILED' ? '#b91c1c' : '#b45309';
  const statusBg = statusStr === 'COMPLETED' ? '#ecfdf5' : statusStr === 'FAILED' ? '#fef2f2' : '#fffbeb';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Receipt - ${tx.reference || tx.id}</title>
  <style>
    @page { size: A4 portrait; margin: 15mm; }
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      color: #1f2937;
      background: #f8fafc;
      margin: 0;
      padding: 32px 16px;
      -webkit-font-smoothing: antialiased;
    }
    .receipt-container {
      max-width: 600px;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      padding: 36px;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #f1f5f9;
      padding-bottom: 24px;
      margin-bottom: 24px;
    }
    .brand h1 {
      margin: 0;
      font-size: 22px;
      font-weight: 800;
      color: #ff6b35;
      letter-spacing: -0.5px;
    }
    .brand p {
      margin: 4px 0 0;
      font-size: 13px;
      color: #64748b;
      font-weight: 500;
    }
    .receipt-badge {
      text-align: right;
    }
    .badge {
      display: inline-block;
      padding: 6px 14px;
      border-radius: 9999px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.5px;
      background: ${statusBg};
      color: ${statusColor};
      border: 1px solid ${statusColor}33;
    }
    .hero-amount {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 24px;
      text-align: center;
      margin-bottom: 28px;
    }
    .hero-amount .label {
      font-size: 12px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .hero-amount .value {
      font-size: 34px;
      font-weight: 800;
      color: #0f172a;
      margin-top: 6px;
      letter-spacing: -0.5px;
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px 24px;
      margin-bottom: 24px;
    }
    .field-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      color: #94a3b8;
      margin-bottom: 4px;
      letter-spacing: 0.5px;
    }
    .field-value {
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
      word-break: break-word;
    }
    .section-title {
      font-size: 12px;
      font-weight: 700;
      color: #475569;
      border-top: 1px solid #f1f5f9;
      padding-top: 20px;
      margin-top: 16px;
      margin-bottom: 16px;
      text-transform: uppercase;
      letter-spacing: 0.75px;
    }
    .running-balance-box {
      background: #ecfdf5;
      border: 1px solid #a7f3d0;
      border-radius: 10px;
      padding: 14px 18px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }
    .running-balance-box .title {
      font-size: 13px;
      font-weight: 600;
      color: #065f46;
    }
    .running-balance-box .balance {
      font-size: 18px;
      font-weight: 800;
      color: #047857;
    }
    .footer {
      border-top: 2px dashed #e2e8f0;
      padding-top: 20px;
      margin-top: 28px;
      text-align: center;
      font-size: 11px;
      color: #94a3b8;
      line-height: 1.6;
    }
    @media print {
      body { background: #ffffff; padding: 0; }
      .receipt-container { border: none; box-shadow: none; padding: 0; max-width: 100%; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="receipt-container">
    <div class="header">
      <div class="brand">
        <h1>ESN FLEET</h1>
        <p>Official Transaction Receipt</p>
      </div>
      <div class="receipt-badge">
        <span class="badge">${statusStr}</span>
        <div style="font-size: 11px; color: #94a3b8; margin-top: 6px;">${formattedDate} ${formattedTime}</div>
      </div>
    </div>

    <div class="hero-amount">
      <div class="label">Total Amount</div>
      <div class="value">${amountStr}</div>
    </div>

    <div class="running-balance-box">
      <span class="title">Account Running Balance</span>
      <span class="balance">${runningBalStr}</span>
    </div>

    <div class="grid">
      <div>
        <div class="field-label">Reference ID</div>
        <div class="field-value">${tx.reference || tx.id}</div>
      </div>
      <div>
        <div class="field-label">Customer ID (CID)</div>
        <div class="field-value" style="color: #ff6b35;">${cid}</div>
      </div>
      <div>
        <div class="field-label">Company / Account</div>
        <div class="field-value">${companyName}</div>
      </div>
      <div>
        <div class="field-label">User / Email</div>
        <div class="field-value">${userEmail}</div>
      </div>
      <div>
        <div class="field-label">Transaction Type</div>
        <div class="field-value" style="text-transform: capitalize;">${(tx.type || '').replace(/_/g, ' ')}</div>
      </div>
      <div>
        <div class="field-label">Date & Time</div>
        <div class="field-value">${formattedDate} ${formattedTime}</div>
      </div>
    </div>

    ${station !== 'N/A' || vehicle !== 'N/A' || litres ? `
    <div class="section-title">Fuel & Vehicle Details</div>
    <div class="grid">
      <div>
        <div class="field-label">Vehicle Plate</div>
        <div class="field-value">${vehicle}</div>
      </div>
      <div>
        <div class="field-label">Merchant / Station</div>
        <div class="field-value">${station}</div>
      </div>
      ${litres ? `
      <div>
        <div class="field-label">Litres Dispensed</div>
        <div class="field-value">${litres}</div>
      </div>` : ''}
      ${price ? `
      <div>
        <div class="field-label">Price / Litre</div>
        <div class="field-value">${price}</div>
      </div>` : ''}
      ${odometer ? `
      <div>
        <div class="field-label">Odometer Reading</div>
        <div class="field-value">${odometer}</div>
      </div>` : ''}
    </div>
    ` : ''}

    ${tx.description ? `
    <div style="margin-bottom: 20px;">
      <div class="field-label">Description / Remarks</div>
      <div class="field-value" style="font-weight: 500; font-size: 13px; color: #475569;">${tx.description}</div>
    </div>
    ` : ''}

    <div class="footer">
      <p>Thank you for using ESN Fleet Services.</p>
      <p>This is an automated computer-generated receipt issued on ${new Date().toLocaleString('en-GB')}.</p>
    </div>
  </div>
</body>
</html>`;
};

export const downloadReceiptFile = (tx: AdminTransaction): void => {
  const htmlContent = generateReceiptHtml(tx);
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ESN_Receipt_${tx.reference || tx.id}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const printReceipt = (tx: AdminTransaction): void => {
  const htmlContent = generateReceiptHtml(tx);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 400);
  }
};
